import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { priorityProjectColors, stageProjectColors } from "@/lib/task-utils";
import { formatString } from "@/lib/string-utils";
import { CodeService, type Code } from "@/services/CodeService";
import { type Project } from "@/services/ProjectService";
import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form"
import { ValidationError } from "@/types/problemDetails";
import { setFormErrors } from "@/lib/react-form-utils";

interface ProjectFormProps {
    project: Project
    actionButtonText?: string
    onSave: (project: Project) => Promise<void>
}

const ProjectForm: React.FC<ProjectFormProps> = ({project, actionButtonText, onSave}) => {
  const {register, handleSubmit, formState: { isDirty, errors }, reset, control, setError } = useForm<Omit<Project, "id">>();
  const [codes, setCodes] = useState<Code[]>([])
  const [priorityCodeId, stageCodeId] = useWatch({ control, name: ["priorityCodeId", "stageCodeId"] })
  const updateButtonText = actionButtonText ?? "Save"
  const isSaveDisabled = !isDirty;
  
  useEffect(() => {
    const initPage = async () => {
      const codes = await CodeService.getCodes2()
      setCodes(codes)
    }
    initPage()
  }, [])

  useEffect(() => {
    reset({
      siteId: project.siteId,
      endDate: formatString(project.endDate?.toString()),
      name: project.name,
      staffDays: project.staffDays,
      cost: project.cost,
      priorityCodeId: project.priorityCodeId,
      startDate: formatString(project.startDate?.toString()),
      stageCodeId: project.stageCodeId
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project])

  const onSubmit = async (data: Omit<Project, "id">) => {
    
    const updatedProject: Project = {
      id: project.id, // Simple ID generation; use UUID in production
      ...data,
    };
    try {
        await onSave(updatedProject);
    } catch (error) {
        if (error instanceof ValidationError) {
            setFormErrors<Omit<Project, "id">>(error.errors, setError, data)
        }
    }
  }; 

    return (
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <Label
            htmlFor="name"
            className="block text-sm font-medium text-gray-700"
          >
            Name*
          </Label>
          <Input
            type="text"
            {...register("name", { required: 'Name is required'})}
          />
          <span className="text-sm text-red-500">{errors.name?.message}</span>
        </div>
        <div>
          <Label
            htmlFor="cost"
            className="block text-sm font-medium text-gray-700"
          >
            Cost
          </Label>
          <Input
            type="number"
            id="cost"
            {...register("cost", { required: 'Cost is required'})}
          />
          <span className="text-sm text-red-500">{errors.cost?.message}</span>
        </div>        
        <div>
          <Label
            htmlFor="staffDays"
          >
            Staff Days
          </Label>
          <Input
            type="number"
            id="staffDays"
            {...register("staffDays", { required: 'Staff days is required'})}
          />
          <span className="text-sm text-red-500">{errors.staffDays?.message}</span>
        </div>
        <div>
          <Label
            htmlFor="priorityCodeId"
          >
            Priority
          </Label>
          <select
            id="priorityCodeId"
            className={`flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1 ${priorityProjectColors[priorityCodeId]}`}
            {...register("priorityCodeId", { required: 'Priority is required'})}
          >
              {codes.length > 0 && (
                <>
                  {codes
                    .filter((c) => c.type === "ProjectPriority")
                    .map((priorityCode) => (
                      <option key={priorityCode.id} value={priorityCode.id} className={priorityProjectColors[priorityCode.id]}>
                        {priorityCode.name}
                      </option>
                    ))}
                </>
              )}
          </select>
        </div>
        <div>
          <Label
            htmlFor="stageCodeId"
            className="block text-sm font-medium text-gray-700"
          >
            Stage
          </Label>
          <select
            id="stageCodeId"
            className={`flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1 ${stageProjectColors[stageCodeId]}`}
            {...register("stageCodeId", { required: 'Stage is required'})}
          >
              {codes.length > 0 && (
                <>
                  {codes
                    .filter((c) => c.type === "ProjectStage")
                    .map((stageCode: Code) => (
                      <option key={stageCode.id} value={stageCode.id ?? ""} className={stageProjectColors[stageCode.id]}>
                        {stageCode.name}
                      </option>
                    ))}
                </>
              )}
          </select>
        </div>
        <div>
          <Label
            htmlFor="startDate"
            className="block text-sm font-medium text-gray-700"
          >
            Start Date
          </Label>
          <Input
            type="date"
            id="startDate"
            {...register("startDate", { required: 'Start date is required'})}
            className="block"
          />
        </div>
        <div>
          <Label
            htmlFor="endDate"
            className="block text-sm font-medium text-gray-700"
          >
            End Date
          </Label>
          <Input
            type="date"
            id="endDate"
            {...register("endDate", { required: 'End date is required'})}
            className="block"
          />
          <span className="text-sm text-red-500">{errors.endDate?.message}</span>
        </div>
        <div className="flex justify-end space-x-2">
          <button
            type="reset"
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Cancel
          </button>
          <Button
            type="submit"
            disabled={isSaveDisabled}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {updateButtonText}
          </Button>
        </div>
      </form>)
}

export default ProjectForm;
